import { PathStyleExtension } from "@deck.gl/extensions";
import { PathLayer, PathLayerProps } from "@deck.gl/layers";
import { Feature } from "geojson";
import { useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";

import { guiShouldAllowSelectingAssetsOrPoints } from "../../state/guiStateHelper";
import { PIPE_TYPES, PipeConfig, getLayers } from "../../state/layerOneDMenu";
import { getSelectedAsset } from "../../state/selectedAsset";
import { Asset } from "../../types/assets";

import { getLayerColors } from "./DeckGlConsts";
import simplify from "./helpers/simplify";

const useLineLayer = (
  setSelectedAsset: (id: string) => void,
  assets?: { [value: string]: Asset[] }
): PathLayer[] => {
  const isSelectable = useSelector(guiShouldAllowSelectingAssetsOrPoints);
  const oneDLayersStyle = useSelector(getLayers);
  const COLORS = getLayerColors(oneDLayersStyle);
  const selectedAsset = useSelector(getSelectedAsset);

  const selectItem = (feature: Feature) => {
    if (feature && feature.properties && isSelectable) {
      setSelectedAsset(feature.properties.site_id);
    }
  };

  const getPath = (coords: XYLocation[]) => simplify(coords, 0.00001, true).flat().flat();

  const getPipeStyle = (pipe: Asset, getKey: keyof PipeConfig) =>
    oneDLayersStyle.pipes.pipeStyles &&
    pipe.properties.model_type === "pipes" &&
    oneDLayersStyle.pipes.pipeStyles[PIPE_TYPES[pipe.properties.sewerage_type]][getKey];

  const defaultProps = {
    id: "default-id",
    data: [],
    pickable: true,
    widthMaxPixels: 15,
    getPath: (d) => {
      if (!("line_geometry" in d.properties) || !d.properties.line_geometry)
        return getPath(d.geometry.coordinates as XYLocation[]);
      // This is only for pumps
      const [coord1, coord2, coord3, coord4] = d.properties.line_geometry.coordinates;
      return getPath([
        [coord1, coord2],
        [coord3, coord4],
      ]);
    },
    positionFormat: `XY`,
    _pathType: "open",
    opacity: 1,
    capRounded: true,
    jointRounded: true,
    onClick: (info) => selectItem(info.object),
    getWidth: 2,
    widthMinPixels: 2,
  } as PathLayerProps<Asset>;

  const dashSettingProps = {
    getDashArray: (asset: Asset) => (getPipeStyle(asset, "dashed") ? [5, 5] : [0, 0]),
    dashGapPickable: true,
    extensions: [new PathStyleExtension({ dash: true })],
  };

  if (!assets) return [];

  return [
    new PathLayer({
      ...defaultProps,
      id: "pumps-line-layer",
      data: assets.pumps.filter(
        (d) => "line_geometry" in d.properties && d.properties.line_geometry
      ),
      getColor: COLORS.PUMP_LINE,
      visible: oneDLayersStyle.pumps.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "channels-layer",
      data: assets.channels,
      getColor: COLORS.CHANNELS,
      visible: oneDLayersStyle.channels.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "levees-layer",
      data: assets.levees,
      getColor: COLORS.LEVEES,
      visible: oneDLayersStyle.levees.visible,
      getWidth: 1,
      widthMinPixels: 1,
    }),
    new PathLayer({
      ...defaultProps,
      ...dashSettingProps,
      id: "pipes-layer",
      data: assets.pipes.filter((pipe) => getPipeStyle(pipe, "visible")),
      getColor: (d) => {
        return COLORS[
          `PIPES_${
            PIPE_TYPES[d.properties.sewerage_type]
          }` as `PIPES_${(typeof PIPE_TYPES)[keyof typeof PIPE_TYPES]}`
        ];
      },
      visible: oneDLayersStyle.pipes.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "culverts-layer",
      data: assets.culverts,
      getColor: COLORS.CULVERTS,
      visible: oneDLayersStyle.culverts.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "orifices-layer",
      data: assets.orifices,
      getColor: COLORS.ORIFICES,
      visible: oneDLayersStyle.orifices.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "breaches-layer",
      data: assets.breaches,
      getColor: COLORS.BREACHES,
      visible: oneDLayersStyle.breaches.visible,
    }),
    new PathLayer({
      ...defaultProps,
      id: "weirs-layer",
      data: assets.weirs,
      getColor: COLORS.WEIRS,
      visible: oneDLayersStyle.weirs.visible,
      getWidth: 1,
      widthMinPixels: 1,
    }),
    new PathLayer({
      ...defaultProps,
      ...dashSettingProps,
      id: "selected-layer",
      data: selectedAsset ? [selectedAsset] : [],
      getColor: COLORS.SELECTED,
      visible: !!selectedAsset,
      getWidth:
        selectedAsset?.properties.model_type === "weirs" ||
        selectedAsset?.properties.model_type === "levees"
          ? 1
          : 2,
      widthMinPixels:
        selectedAsset?.properties.model_type === "weirs" ||
        selectedAsset?.properties.model_type === "levees"
          ? 1
          : 2,
      updateTriggers: {
        getColor: selectedAsset,
        getDashArray: selectedAsset,
      },
    }),
  ];
};

export default useLineLayer;
